import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import { computeOrderStatus } from '../services/orders';

export type BannerRole = 'player' | 'coach';

/**
 * What kind of action the banner CTA invokes. The helper stays free of
 * navigation / panel-open side effects — the caller dispatches on
 * `intent` and supplies the actual handler.
 *
 *   upload    — player uploads the next reserved lesson's video
 *   record    — coach opens the lesson editor for the in-progress lesson
 *   watch     — player watches a just-delivered lesson
 *   book-next — player books another program from the coach (post-all-delivered)
 */
export type BannerCtaIntent = 'upload' | 'record' | 'watch' | 'book-next';

export interface BannerCta {
  label: string;
  intent: BannerCtaIntent;
  /** The lesson the CTA references (when one already exists). Null for
   *  intents that target a not-yet-created lesson (e.g. upload after a
   *  delivered lesson with no next-reserved yet — lazy-create flow) or
   *  that don't reference a specific lesson (e.g. book-next). */
  lesson: Lesson | null;
  /** Target lesson number for upload-intent CTAs. Drives the upload
   *  modal's `nextLessonNum` without requiring a pre-existing lesson
   *  doc — covers the watch-pending → lazy-create case. */
  lessonNumber?: number;
}

export interface BannerState {
  /** Dominant, scream-moment line. e.g. "Lesson 2 ready" or "Ready to start". */
  headline: string;
  /** Contextual line below the headline, ~75% opacity in the banner chrome. */
  subline: string;
  /** Total lessons in the program — drives the capsule count (capped at 6 by the renderer). */
  totalLessons: number;
  /** Number of progress capsules filled. */
  filledDots: number;
  /** Action descriptor; null when no action is appropriate (e.g. player after submission, coach idle states). */
  cta: BannerCta | null;
}

export interface DeriveBannerStateOpts {
  order: Order;
  lessonsForOrder: Lesson[];
  role: BannerRole;
  otherPartyFirstName: string;
}


/**
 * Pick which training-program order should drive the banner for a
 * conversation. Newest non-refunded program first; prefers the one
 * still active (has undelivered lessons); falls back to the most
 * recent program if every program is fully delivered.
 *
 * Naturally handles auto-renew: as soon as a new active program order
 * exists for the player+coach pair, this rule picks it, so the banner
 * switches to "Lesson 1 of [new program]" without the previous program's
 * all-delivered state lingering.
 *
 * Returns null if no qualifying program exists (e.g., single-lesson
 * conversation, support thread, or every program is refunded).
 */
export function pickBannerProgram(
  orders: Order[],
  allLessons: Lesson[],
): Order | null {
  const programs = orders
    .filter(o => o.type === 'training_program')
    .filter(o => o.paymentStatus !== 'refunded')
    // Newest first so the active picker prefers the latest active program.
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (programs.length === 0) return null;

  const active = programs.find(
    o => computeOrderStatus(o, allLessons) !== 'complete',
  );
  return active ?? programs[0];
}
